import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FaChevronLeft } from 'react-icons/fa'
import EditableNameList, { NameListItem } from './EditableNameList'
import {
  addNotifiableWorkDocument,
  createNotifiableWork,
  deleteNotifiableWork,
  deleteNotifiableWorkDocument,
  getNotifiableWorksForAdmin,
  NotifiableWorkAdmin,
  renameNotifiableWork,
  renameNotifiableWorkDocument,
  reorderNotifiableWorkDocuments,
  updateNotifiableWorkDocument,
} from '../../data/lookupAdminData'
import { useToast } from '../../contexts/ToastContext'
import { getApiErrorDetails } from '../../helpers/apiError'
import './Lookups.css'

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`

/**
 * The notifiable works offered on a job, and the documents each one needs.
 * Pick a notifiable work on the left to manage its documents on the right.
 */
const NotifiableWorksAdmin = () => {
  const navigate = useNavigate()
  const { showToast } = useToast()

  const [works, setWorks] = useState<NotifiableWorkAdmin[] | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [selectedId, setSelectedId] = useState<number | null>(null)

  const load = useCallback(async () => {
    try {
      const result = await getNotifiableWorksForAdmin()
      setWorks(result)
      setSelectedId((current) =>
        result.some((w) => w.notifiableWorkId === current) ? current : result[0]?.notifiableWorkId ?? null
      )
    } catch (error) {
      setLoadError(getApiErrorDetails(error, "The notifiable works couldn't be loaded.").message)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  // Every change reloads, so job counts and ordering stay true to the server.
  // An empty success message saves quietly - reordering is click after click.
  const attempt = async (work: () => Promise<unknown>, success: string, failure: string) => {
    try {
      await work()
      if (success) showToast(success, 'success')
      await load()
      return true
    } catch (error) {
      showToast(getApiErrorDetails(error, failure).message, 'error')
      return false
    }
  }

  const selected = works?.find((w) => w.notifiableWorkId === selectedId) ?? null

  const workItems: NameListItem[] = (works ?? []).map((w) => ({
    id: w.notifiableWorkId,
    name: w.name,
    meta: `${plural(w.jobCount, 'job')} · ${plural(w.documents.length, 'document')}`,
  }))

  const documentItems: NameListItem[] = (selected?.documents ?? []).map((d) => ({
    id: d.notifiableWorkDocumentId,
    name: d.name,
  }))

  return (
    <div className='lk-page'>
      <div className='lk-inner'>
        <button type='button' className='lk-crumb' onClick={() => navigate('/management')}>
          <FaChevronLeft /> Management
        </button>
        <h1 className='lk-title'>Notifiable Works</h1>
        <p className='lk-lead'>
          The notifiable works that can be chosen on a job, and the documents each one needs.
        </p>

        {loadError ? (
          <div className='lk-error'>{loadError}</div>
        ) : !works ? (
          <p className='lk-muted'>Loading…</p>
        ) : (
          <div className='lk-layout lk-layout--two'>
            <section className='lk-card'>
              <h2 className='lk-subtitle'>Notifiable works</h2>
              <p className='lk-hint'>
                Renaming one renames it on every job that has it. Removing one stops it being offered; jobs that
                already have it keep it.
              </p>
              <EditableNameList
                items={workItems}
                selectedId={selectedId}
                onSelect={(item) => setSelectedId(item.id)}
                addPlaceholder='New notifiable work'
                emptyText='There are no notifiable works yet.'
                maxLength={100}
                onAdd={(name) =>
                  attempt(
                    async () => setSelectedId((await createNotifiableWork(name)).notifiableWorkId),
                    `Added ${name}`,
                    "The notifiable work couldn't be added."
                  )
                }
                onRename={(item, name) => {
                  const work = works.find((w) => w.notifiableWorkId === item.id)
                  if (
                    work &&
                    work.jobCount > 0 &&
                    !window.confirm(`Rename "${item.name}" to "${name}"? It will be renamed on ${plural(work.jobCount, 'job')} too.`)
                  ) {
                    return Promise.resolve(false)
                  }
                  return attempt(() => renameNotifiableWork(item.id, name), 'Renamed', "It couldn't be renamed.")
                }}
                onDelete={async (item) => {
                  const work = works.find((w) => w.notifiableWorkId === item.id)
                  const kept = work && work.jobCount > 0 ? ` The ${plural(work.jobCount, 'job')} that have it will keep it.` : ''
                  if (!window.confirm(`Remove "${item.name}"? It won't be offered on jobs any more.${kept}`)) return
                  await attempt(() => deleteNotifiableWork(item.id), `Removed ${item.name}`, "It couldn't be removed.")
                }}
              />
            </section>

            <section className='lk-card'>
              {selected ? (
                <>
                  <h2 className='lk-subtitle'>Documents for {selected.name}</h2>
                  <p className='lk-hint'>
                    The documents this notifiable work needs, in the order they're listed on a job. Drag them, or use
                    the arrows, to change the order. Only <strong>Required</strong> documents count towards the work's
                    badge on a job. A <strong>File upload</strong> is done by uploading a file; anything else is ticked
                    off on the job with a switch.
                  </p>
                  <EditableNameList
                    items={documentItems}
                    addPlaceholder='New document'
                    emptyText='No documents yet.'
                    maxLength={200}
                    onAdd={(name) =>
                      attempt(
                        () => addNotifiableWorkDocument(selected.notifiableWorkId, name),
                        `Added ${name}`,
                        "The document couldn't be added."
                      )
                    }
                    onRename={(item, name) =>
                      attempt(() => renameNotifiableWorkDocument(item.id, name), 'Renamed', "The document couldn't be renamed.")
                    }
                    renderControls={(item) => {
                      const document = selected.documents.find((d) => d.notifiableWorkDocumentId === item.id)
                      if (!document) return null
                      const change = (changes: { isRequired?: boolean; isFileUpload?: boolean }) =>
                        attempt(
                          () => updateNotifiableWorkDocument(item.id, changes),
                          '',
                          "The document couldn't be updated."
                        )
                      return (
                        <>
                          <label className='lk-toggle'>
                            <input
                              type='checkbox'
                              checked={document.isRequired}
                              onChange={(e) => change({ isRequired: e.target.checked })}
                            />
                            Required
                          </label>
                          <label className='lk-toggle'>
                            <input
                              type='checkbox'
                              checked={document.isFileUpload}
                              onChange={(e) => change({ isFileUpload: e.target.checked })}
                            />
                            File upload
                          </label>
                        </>
                      )
                    }}
                    onReorder={(ids) =>
                      attempt(
                        () => reorderNotifiableWorkDocuments(selected.notifiableWorkId, ids),
                        '',
                        "The new order couldn't be saved."
                      )
                    }
                    onDelete={async (item) => {
                      if (!window.confirm(`Delete the "${item.name}" document from ${selected.name}?`)) return
                      await attempt(() => deleteNotifiableWorkDocument(item.id), `Deleted ${item.name}`, "The document couldn't be deleted.")
                    }}
                  />
                </>
              ) : (
                <p className='lk-muted'>Add a notifiable work to manage its documents.</p>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  )
}

export default NotifiableWorksAdmin
